import { ConditionalRender } from '@/components/Helpers/ComponentRender/ConditionalRender';
import { EventTracker } from '@/utils';
import { ScoreCard } from '@design-system/Cards/ScoreCard';
import { StandardContainer } from '@design-system/StandardContainer';
import { SkeletonView } from '@screens/HealthConnections/HealthConnectionsContainer';
import { HealthDetailsSectionHeader } from '@screens/HealthPages/HealthDetailsSectionHeader';
import { DoctorsNotes } from '@screens/LevelTwoDetail/DoctorsNotes';
import {
  useGetContributingScores,
  useGetDoctorsNotes,
  useGetScoreDetails,
} from '@viome/viome-base/src/usecase/Health/HealthQuery';
import { useNavigate, useParams } from 'react-router-dom';
import DoctorsNoteShimmer from '../LevelTwoDetail/DoctorsNoteShimmer';

export const HealthL3DetailsPage = () => {
  const navigate = useNavigate();
  const { id: scoreId } = useParams();

  const { data: l3ScoreDetail, isLoading: isL3ScoreDetailsAPILoading } =
    useGetScoreDetails('L3', Number(scoreId));

  const {
    data: contributingScoreData,
    isLoading: isL3ContributingScoreAPILoading,
  } = useGetContributingScores('L3', Number(scoreId));

  const {
    data: doctorNotes,
    isLoading: isDoctorsNoteLoading,
    isError: isDoctorsNoteError,
  } = useGetDoctorsNotes('L3', Number(scoreId));

  const navigateToConditionsPage = (id: number, type: string) => {
    return navigate(type === 'L3' ? `/health/l3/${id}` : `/health/l4/${id}`);
  };

  const renderDoctorsNotWithBg = () => {
    if (!doctorNotes) return;
    return (
      <section
        className="bg-black bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) 30%, rgba(0, 0, 0, 1) 100%), url('https://cdn-st.stage.azure.viome.com/viome-cdn/media/app/v2/png/home/image_for_doctors_notes.png')`,
        }}
      >
        <StandardContainer>
          <DoctorsNotes notes={doctorNotes} />
        </StandardContainer>
      </section>
    );
  };

  return (
    <div className="bg-black h-full text-primary w-full dark !max-w-full">
      <HealthDetailsSectionHeader
        scoreDetail={l3ScoreDetail}
        isScoreDetailsAPILoading={isL3ScoreDetailsAPILoading}
        scoreId={Number(scoreId)}
      />
      <ConditionalRender
        // Doctors note conditional rendering
        conditions={[
          { when: isDoctorsNoteLoading, render: <DoctorsNoteShimmer /> },
          { when: isDoctorsNoteError, render: null },
        ]}
        otherwise={renderDoctorsNotWithBg()}
      />{' '}
      <section>
        <div className="mb-24">
          <StandardContainer>
            {isL3ContributingScoreAPILoading ? (
              <SkeletonView />
            ) : (
              <>
                {contributingScoreData && contributingScoreData.length > 0 && (
                  <div className="mt-12">
                    <div className="typography-h1 text-primary">
                      Contributing Scores
                    </div>
                    <div className="typography-body text-secondary mt-4 max-w-[35rem]">
                      This score is calculated using the contributing scores
                      listed below.
                    </div>
                    <div className="grid lg:grid-cols-2 gap-2.5 md:grid-cols-1 mb-6 mt-6">
                      {contributingScoreData?.map((zone) => (
                        <div key={`${zone.id}`}>
                          <ScoreCard
                            title={zone.title}
                            tag={zone.rank}
                            color={zone.rank_color || ''}
                            score={zone.scores}
                            variant="dark"
                            onClick={() => {
                              EventTracker.trackEvent(
                                'health',
                                `health_contributingScores_${zone.title.replace(/\s+/g, '')}`,
                              );
                              navigateToConditionsPage(
                                zone.id,
                                zone.score_type,
                              );
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </StandardContainer>
        </div>
      </section>
    </div>
  );
};
